// Endless carousel reproducing the former GemPages (Owl Carousel) settings:
// - one slide per step, 1.2s "ease" for autoplay (every `data-speed` s) and arrows;
// - touch drag only (no mouse drag), released to the next slide in the drag direction
//   once pulled past 30px, 250ms per slide crossed;
// - true loop: a copy of every slide sits on each side of the real ones, and the
//   position jumps back onto the real slides after each move.
// Autoplay pauses on hover, touch and keyboard focus, and is off with reduced motion
// or in the theme editor. Dawn's slideshow-component assumes one visible slide.
if (!customElements.get('pp-autoplay-slider')) {
  customElements.define(
    'pp-autoplay-slider',
    class PpAutoplaySlider extends HTMLElement {
      static AUTOPLAY_SPEED = 1200;
      static DRAG_SPEED = 250;
      static PULL = 30;

      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        if (!this.track) return;
        this.viewport = this.track.parentElement;

        this.slides = [...this.track.children];
        this.count = this.slides.length;
        if (this.count < 2) return;

        this.delay = Number(this.dataset.speed || 3) * 1000;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.index = 0;
        this.moving = false;
        this.interacting = false;
        this.dragged = false;

        const copy = (slide) => {
          const clone = slide.cloneNode(true);
          // Not inert: inert slides are skipped by hit-testing, so a touch on a visible copy
          // would land on the viewport and pan the page instead of dragging the track.
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((el) => el.setAttribute('tabindex', '-1'));
          [...clone.attributes].filter(({ name }) => name.startsWith('data-shopify')).forEach(({ name }) => clone.removeAttribute(name));
          return clone;
        };
        this.track.prepend(...this.slides.map(copy));
        this.track.append(...this.slides.map(copy));

        this.querySelector('button[name="previous"]')?.addEventListener('click', () => this.go(-1));
        this.querySelector('button[name="next"]')?.addEventListener('click', () => this.go(1));

        this.addEventListener('mouseenter', () => this.setInteracting(true));
        this.addEventListener('mouseleave', () => this.setInteracting(this.contains(document.activeElement)));
        this.addEventListener('focusin', (event) => {
          this.setInteracting(true);
          this.reveal(event.target);
        });
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) this.setInteracting(false);
        });
        this.addEventListener('shopify:block:select', (event) => this.reveal(event.target));
        document.addEventListener('visibilitychange', () => this.sync());

        // On the viewport: the track box is translated away, only its slides and gaps show.
        this.viewport.addEventListener('pointerdown', (event) => this.drag(event));
        this.viewport.addEventListener(
          'click',
          (event) => {
            if (!this.dragged) return;
            event.preventDefault();
            event.stopPropagation();
          },
          true
        );
        this.track.addEventListener('transitionend', (event) => {
          if (event.target === this.track) this.settle();
        });

        // Width only: lazy images change the track height while a move is running.
        let width = this.track.clientWidth;
        new ResizeObserver(() => {
          if (this.track.clientWidth === width) return;
          width = this.track.clientWidth;
          this.place(0);
          this.sync();
        }).observe(this.track);

        this.place(0);
        this.sync();
      }

      disconnectedCallback() {
        clearInterval(this.timer);
        clearTimeout(this.settleTimer);
      }

      get step() {
        const [first, second] = this.track.children;
        return second.offsetLeft - first.offsetLeft;
      }

      setX(x, duration) {
        this.track.style.transition = duration ? `transform ${duration}ms ease` : 'none';
        this.track.style.transform = `translateX(${x}px)`;
      }

      place(duration) {
        const time = this.reducedMotion ? 0 : duration;
        this.moving = time > 0;
        this.setX(-(this.count + this.index) * this.step, time);
        clearTimeout(this.settleTimer);
        if (time) {
          // Fallback in case transitionend never fires (section hidden mid-move).
          this.settleTimer = setTimeout(() => this.settle(), time + 100);
        } else {
          this.settle();
        }
      }

      // Back onto the real slides once a move has ended.
      settle() {
        this.moving = false;
        const index = ((this.index % this.count) + this.count) % this.count;
        if (index === this.index) return;
        this.index = index;
        this.setX(-(this.count + index) * this.step, 0);
      }

      go(delta) {
        if (this.moving) return;
        this.index += delta;
        this.place(PpAutoplaySlider.AUTOPLAY_SPEED);
      }

      reveal(target) {
        const slide = this.slides.indexOf(target.closest('[data-track] > *'));
        if (slide === -1) return;
        const visible = Math.max(1, Math.round(this.track.clientWidth / this.step));
        if (slide >= this.index && slide < this.index + visible) return;
        this.index = slide;
        this.place(PpAutoplaySlider.DRAG_SPEED);
      }

      drag(start) {
        if (start.pointerType === 'mouse') return;
        this.dragged = false;
        this.setInteracting(true);

        // Grab the track where it is, even mid-move, so successive swipes chain.
        const { step, count } = this;
        const interrupted = this.moving;
        clearTimeout(this.settleTimer);
        this.moving = false;
        let origin = new DOMMatrix(getComputedStyle(this.track).transform).m41;
        const grabbed = -origin / step - count;
        // Copies mirror the real slides: jump a whole set back onto the real ones.
        let shift = 0;
        if (grabbed >= count) shift = -count;
        if (grabbed < 0) shift = count;
        origin -= shift * step;
        this.setX(origin, 0);
        const from = Math.round(-origin / step - count);
        // A swipe during a move counts from where that move was heading, so quick
        // successive swipes each add one slide.
        const base = interrupted ? this.index + shift : from;
        let distance = 0;
        let dragging = false;

        const move = (event) => {
          distance = event.clientX - start.clientX;
          if (!dragging && Math.abs(distance) > 3) dragging = true;
          if (dragging) this.setX(origin + distance, 0);
        };
        const end = (event) => {
          this.viewport.removeEventListener('pointermove', move);
          this.viewport.removeEventListener('pointerup', end);
          this.viewport.removeEventListener('pointercancel', end);

          if (dragging) {
            this.dragged = true;
            setTimeout(() => (this.dragged = false), 400);
            // Owl's rule: past the 30px pull, go on in the drag direction.
            const pulled = event.type === 'pointercancel' ? 0 : Math.abs(distance);
            const slides = pulled % step < PpAutoplaySlider.PULL ? Math.floor(pulled / step) : Math.ceil(pulled / step);
            const position = -(origin + distance) / step - count;
            this.index = base + (distance < 0 ? slides : -slides);
            this.place(Math.min(Math.max(Math.ceil(Math.abs(this.index - position)), 1), 6) * PpAutoplaySlider.DRAG_SPEED);
          } else if (interrupted) {
            this.index = base;
            this.place(PpAutoplaySlider.DRAG_SPEED);
          }
          this.setInteracting(this.contains(document.activeElement));
        };

        this.viewport.addEventListener('pointermove', move);
        this.viewport.addEventListener('pointerup', end);
        this.viewport.addEventListener('pointercancel', end);
      }

      setInteracting(value) {
        this.interacting = value;
        this.sync();
      }

      sync() {
        const contentWidth = this.count * this.step - (this.step - this.slides[0].offsetWidth);
        this.toggleAttribute('data-scrollable', contentWidth > this.track.clientWidth + 1);

        clearInterval(this.timer);
        const editor = window.Shopify && Shopify.designMode;
        if (!this.reducedMotion && !this.interacting && !document.hidden && !editor) {
          this.timer = setInterval(() => this.go(1), this.delay);
        }
      }
    }
  );
}
